// src/modules/admin/SupervisorAssignments.js
import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { adminService } from '../../services/adminService';
import { supervisorService } from '../../services/supervisorService';
import { permissionService } from '../../services/permissionService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { useAuth } from '../auth/hooks/useAuth';
import { SupervisorDetailPanel } from './SupervisorDetailPanel';

const ELIGIBLE_SUPERVISOR_ROLES = ['Supervisor', 'Manager', 'Admin'];

export function SupervisorAssignments() {
  const { user: currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [supervisees, setSupervisees] = useState([]);
  const [countries, setCountries] = useState([]);
  const [supervisorCountries, setSupervisorCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // In-flight guard: per-row pending Set so rapid clicks don't race.
  // Keys: `supervisee:${id}` (add / remove / canEdit), `country:${id}`.
  const [pendingOps, setPendingOps] = useState(new Set());

  useAbortableFetch((ctx) => loadData(ctx), []);

  const availableSupervisees = useMemo(
    () =>
      allUsers.filter(
        (u) =>
          !supervisees.some((s) => s.id === u.id) &&
          u.id !== selectedSupervisor?.id
      ),
    [allUsers, supervisees, selectedSupervisor?.id]
  );

  const availableCountries = useMemo(
    () => countries.filter((c) => !supervisorCountries.some((sc) => sc.id === c.id)),
    [countries, supervisorCountries]
  );

  const loadData = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      setError(null);

      const users = await adminService.getAllUsers();
      if (isCancelled()) return;
      setAllUsers(users);

      const countriesData = await adminService.getAllCountries();
      if (isCancelled()) return;
      setCountries(countriesData);
    } catch (err) {
      if (isCancelled()) return;
      setError('Failed to load data: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  const handleSelectSupervisor = async (supervisor) => {
    try {
      setSelectedSupervisor(supervisor);
      setError(null);

      const superviseesList = await permissionService.getSupervisees(supervisor.id);
      setSupervisees(superviseesList);

      const countriesList = await supervisorService.getSupervisorCountries(supervisor.id);
      setSupervisorCountries(countriesList);
    } catch (err) {
      setError('Failed to load supervisor details: ' + mapSupabaseError(err));
      console.error(err);
    }
  };

  const markPending = (key) => setPendingOps((prev) => new Set(prev).add(key));
  const clearPending = (key) =>
    setPendingOps((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  const handleAddSupervisee = async (supervisee) => {
    const key = `supervisee:${supervisee.id}`;
    if (pendingOps.has(key)) return;

    const previousSupervisees = supervisees;
    // Optimistic: insert immediately. canEdit defaults to false (the
    // service default — admins can flip it via the canEdit checkbox).
    setSupervisees((prev) => [...prev, { ...supervisee, canEdit: false }]);
    markPending(key);

    try {
      await supervisorService.assignSupervisee(
        selectedSupervisor.id,
        supervisee.id,
        currentUser.id
      );
    } catch (err) {
      setSupervisees(previousSupervisees);
      toast.error('Failed to add supervisee: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      clearPending(key);
    }
  };

  const handleRemoveSupervisee = async (superviseeId) => {
    const key = `supervisee:${superviseeId}`;
    if (pendingOps.has(key)) return;

    const previousSupervisees = supervisees;
    setSupervisees((prev) => prev.filter((s) => s.id !== superviseeId));
    markPending(key);

    try {
      await supervisorService.removeSupervisee(selectedSupervisor.id, superviseeId);
    } catch (err) {
      setSupervisees(previousSupervisees);
      toast.error('Failed to remove supervisee: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      clearPending(key);
    }
  };

  const handleToggleEditPermission = async (superviseeId, currentValue) => {
    const key = `supervisee:${superviseeId}`;
    if (pendingOps.has(key)) return;

    const previousSupervisees = supervisees;
    setSupervisees((prev) =>
      prev.map((s) => (s.id === superviseeId ? { ...s, canEdit: !currentValue } : s))
    );
    markPending(key);

    try {
      if (currentValue) {
        await supervisorService.disableEditPermission(selectedSupervisor.id, superviseeId);
      } else {
        await supervisorService.enableEditPermission(selectedSupervisor.id, superviseeId);
      }
    } catch (err) {
      setSupervisees(previousSupervisees);
      toast.error('Failed to update edit permission: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      clearPending(key);
    }
  };

  const handleAddCountry = async (countryId) => {
    const key = `country:${countryId}`;
    if (pendingOps.has(key)) return;
    const country = countries.find((c) => c.id === countryId);
    if (!country) return;

    const previousCountries = supervisorCountries;
    setSupervisorCountries((prev) => [...prev, country]);
    markPending(key);

    try {
      await supervisorService.assignCountry(selectedSupervisor.id, countryId, currentUser.id);
    } catch (err) {
      setSupervisorCountries(previousCountries);
      toast.error('Failed to add country: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      clearPending(key);
    }
  };

  const handleRemoveCountry = async (countryId) => {
    const key = `country:${countryId}`;
    if (pendingOps.has(key)) return;

    const previousCountries = supervisorCountries;
    setSupervisorCountries((prev) => prev.filter((c) => c.id !== countryId));
    markPending(key);

    try {
      await supervisorService.removeCountry(selectedSupervisor.id, countryId);
    } catch (err) {
      setSupervisorCountries(previousCountries);
      toast.error('Failed to remove country: ' + mapSupabaseError(err));
      console.error(err);
    } finally {
      clearPending(key);
    }
  };

  if (loading) return <div className="admin-panel">Loading...</div>;

  const supervisorList = allUsers.filter((u) =>
    ELIGIBLE_SUPERVISOR_ROLES.includes(u.role)
  );

  return (
    <div className="supervisor-assignments">
      <h2>Supervisor Assignments</h2>
      <p className="supervisor-help">
        Supervisors oversee assigned users and can review their submissions.
        Supervisors, Managers, and Admins can all have assignees. A user can have
        several supervisors but only one Team Lead, set per user in User Management.
      </p>
      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="assignments-container">
        {/* Left: List of supervisors */}
        <div className="supervisors-list">
          <h3>Supervisors</h3>
          <div className="user-selector">
            {supervisorList.length === 0 && (
              <p className="empty-state">No eligible supervisors found.</p>
            )}
            {supervisorList.map((supervisor) => (
              <button
                key={supervisor.id}
                className={`user-item ${selectedSupervisor?.id === supervisor.id ? 'selected' : ''}`}
                onClick={() => handleSelectSupervisor(supervisor)}
              >
                <div className="user-name">
                  {supervisor.name}{' '}
                  <span className={`badge badge-${supervisor.role.toLowerCase()}`}>
                    {supervisor.role}
                  </span>
                </div>
                <div className="user-email">{supervisor.email}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: Supervisor details */}
        {selectedSupervisor ? (
          <SupervisorDetailPanel
            selectedSupervisor={selectedSupervisor}
            supervisees={supervisees}
            availableSupervisees={availableSupervisees}
            supervisorCountries={supervisorCountries}
            availableCountries={availableCountries}
            allUsers={allUsers}
            pendingOps={pendingOps}
            onAddSupervisee={handleAddSupervisee}
            onRemoveSupervisee={handleRemoveSupervisee}
            onToggleEditPermission={handleToggleEditPermission}
            onAddCountry={handleAddCountry}
            onRemoveCountry={handleRemoveCountry}
          />
        ) : (
          <p className="empty-state">
            Select a supervisor on the left to manage their team.
          </p>
        )}
      </div>
    </div>
  );
}
